import type { SignalDto, TradeDetail, TradeDto } from '@et/shared';
import { useState } from 'react';
import { api, describeError } from '../api/client';
import { dateTime, money, signed, tone } from '../lib/format';
import { useT } from '../lib/i18n';
import { toast } from '../lib/toast';
import { useResource } from '../lib/use-resource';
import { Coin } from '../components/CoinLogo';

interface Page<T> { items: T[]; page: number; pageSize: number; total: number }

export function Trades(): JSX.Element {
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('');
  const [open, setOpen] = useState<TradeDetail | null>(null);
  const t = useT();
  const tr = t;
  const q = `/trades?page=${String(page)}&pageSize=25${status ? `&status=${status}` : ''}`;
  const trades = useResource<Page<TradeDto>>(q);
  const pages = trades.data ? Math.max(1, Math.ceil(trades.data.total / trades.data.pageSize)) : 1;

  const show = async (id: string): Promise<void> => {
    try {
      setOpen(await api.get<TradeDetail>(`/trades/${id}`));
    } catch (err) {
      toast.error(describeError(err));
    }
  };

  return (
    <div className="stack">
      <h1>{t('Trades')}</h1>
      <div className="row">
        <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
          <option value="">{t('All statuses')}</option>
          {['opening', 'open', 'closing', 'closed', 'failed'].map((s) => <option key={s} value={s}>{t(s)}</option>)}
        </select>
        <span className="muted">{t('{n} trades', { n: trades.data?.total ?? 0 })}</span>
      </div>
      <div className="card table-wrap">
        <table>
          <thead><tr><th>{t('Opened')}</th><th>{t('Symbol')}</th><th>{t('Strategy')}</th><th>{t('Status')}</th><th className="num">{t('Entry')}</th><th className="num">{t('Exit')}</th><th className="num">PnL</th><th className="num">%</th><th>{t('Reason')}</th><th /></tr></thead>
          <tbody>
            {trades.data?.items.map((t) => (
              <tr key={t.id}>
                <td className="muted">{dateTime(t.openedAt ?? t.createdAt)}</td>
                <td><Coin symbol={t.symbol} /></td>
                <td className="muted">{tr(t.strategyKey)}</td>
                <td>{tr(t.status)}</td>
                <td className="num">{money(t.entryPrice, 4)}</td>
                <td className="num">{money(t.exitAvgPrice, 4)}</td>
                <td className={`num ${tone(t.realizedPnl)}`}>{signed(t.realizedPnl)}</td>
                <td className={`num ${tone(t.realizedPnlPct)}`}>{signed(t.realizedPnlPct)}</td>
                <td className="muted">{t.closeReason ? tr(t.closeReason) : ''}</td>
                <td><button className="small" onClick={() => void show(t.id)}>{tr('Detail')}</button></td>
              </tr>
            ))}
          </tbody>
        </table>
        {trades.data?.items.length === 0 && <div className="empty">{t('No trades.')}</div>}
        <div className="row" style={{ justifyContent: 'flex-end', marginTop: 8 }}>
          <button className="small" disabled={page <= 1} onClick={() => { setPage(page - 1); }}>‹</button>
          <span className="muted">{page} / {pages}</span>
          <button className="small" disabled={page >= pages} onClick={() => { setPage(page + 1); }}>›</button>
        </div>
      </div>

      {open && (
        <div className="card stack">
          <div className="row"><h2 style={{ margin: 0 }}>{open.symbol} · {t(open.status)}</h2><span className="spacer" style={{ flex: 1 }} /><button className="small" onClick={() => { setOpen(null); }}>{t('Close')}</button></div>
          <div className="table-wrap">
            <table>
              <thead><tr><th>{t('Order')}</th><th>{t('Purpose')}</th><th>{t('Side')}</th><th>{t('Status')}</th><th className="num">{t('Qty')}</th><th className="num">{t('Filled')}</th><th className="num">{t('Avg price')}</th><th className="num">{t('Fee')}</th></tr></thead>
              <tbody>{open.orders.map((o) => (
                <tr key={o.id}><td className="mono">{o.clientOrderId}</td><td>{t(o.purpose)}</td><td>{t(o.side)}</td><td>{t(o.status)}</td><td className="num">{o.requestedQty}</td><td className="num">{o.filledQty}</td><td className="num">{money(o.avgFillPrice, 4)}</td><td className="num">{money(o.feeAmount, 4)}</td></tr>
              ))}</tbody>
            </table>
          </div>
          {open.signal && <><h2>{t('Signal reason')}</h2><pre className="mono" style={{ whiteSpace: 'pre-wrap', fontSize: 12 }}>{JSON.stringify(open.signal.reason, null, 2)}</pre></>}
        </div>
      )}

      <Signals />
    </div>
  );
}

function Signals(): JSX.Element {
  const [status, setStatus] = useState('');
  const t = useT();
  const signals = useResource<SignalDto[]>(`/signals?limit=100${status ? `&status=${status}` : ''}`);
  return (
    <div className="card">
      <div className="row" style={{ marginBottom: 12 }}>
        <h2 style={{ margin: 0 }}>{t('Signals')}</h2>
        <select value={status} onChange={(e) => { setStatus(e.target.value); }}>
          <option value="">{t('All')}</option>
          {['generated', 'confirmed', 'executed', 'rejected', 'expired'].map((s) => <option key={s} value={s}>{t(s)}</option>)}
        </select>
      </div>
      <div className="table-wrap">
        <table>
          <thead><tr><th>{t('Time')}</th><th>{t('Strategy')}</th><th>{t('Symbol')}</th><th className="num">{t('Conf.')}</th><th>{t('Status')}</th><th>{t('Gates')}</th><th>{t('Why')}</th></tr></thead>
          <tbody>{signals.data?.map((s) => {
            const gates = (s.reason?.['gates'] as { gate: string; pass: boolean }[] | undefined) ?? [];
            return (
              <tr key={s.id}>
                <td className="muted">{dateTime(s.createdAt)}</td>
                <td className="muted">{t(s.strategyKey)}</td>
                <td><Coin symbol={s.symbol} /></td>
                <td className="num">{s.confidence}</td>
                <td>{t(s.status)}</td>
                <td>{gates.map((g) => <span key={g.gate} className={g.pass ? 'pos' : 'neg'} style={{ marginRight: 6 }}>{g.pass ? '✓' : '✗'} {g.gate}</span>)}</td>
                <td className="muted" style={{ whiteSpace: 'normal' }}>{s.rejectionReason ?? ''}</td>
              </tr>
            );
          })}</tbody>
        </table>
        {signals.data?.length === 0 && <div className="empty">{t('No signals yet.')}</div>}
      </div>
    </div>
  );
}
